import type { EditUserDocumentPromptResolve } from '@/modules/companies'
import { EditUserDocumentPrompt } from '@/modules/companies'
import { useUserDocumentMutations } from '@/modules/companies/CompanySettings/util/mutations.hooks'
import { useConfirm } from '@/utils/hooks'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { subWeeks } from 'date-fns'
import type { UserDocumentRow } from './types'

/**
 * A document's expiration date is optional and only exists to notify about, so
 * both it and the notification time are cleared when the user turns the
 * notification off. The notification time is stored as an absolute timestamp
 * but entered as "weeks before the expiration date".
 */
function getExpirationAndNotificationTime(
  form: EditUserDocumentPromptResolve['form'],
  hasNotification: boolean
) {
  const expirationDate = !hasNotification ? null : form.expirationDate
  const notificationTime =
    !hasNotification || !expirationDate || !form.notificationTime
      ? null
      : subWeeks(expirationDate, form.notificationTime)

  return { expirationDate, notificationTime }
}

interface Options {
  /** Called with the document to attach files to, right after a create or edit. */
  onShowFiles(document: { id: string; name: string }): void
}

/** Add/edit/delete handlers for the company-wide user documents page. */
export function useUserDocumentHandlers({ onShowFiles }: Options) {
  const translations = useTranslate({
    delete: 'settings.prompts.user-document-delete-title',
    deleteConfirmation: ['settings.prompts.user-document-delete', { name: '' }],
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()

  const mutations = useUserDocumentMutations()

  async function addDocument() {
    const { data } = await addPrompt<EditUserDocumentPromptResolve | null>(
      resolve => <EditUserDocumentPrompt askForUser onSubmit={resolve} />
    )
    if (!data?.form.user) return

    const { expirationDate, notificationTime } =
      getExpirationAndNotificationTime(data.form, data.hasNotification)

    const result = await mutations.create({
      variables: {
        input: {
          // Asked for in the prompt here: unlike a user's own tab, this page
          // has no user of its own to attach the document to.
          user: data.form.user,
          name: data.form.name,
          responsibleUser: data.hasNotification
            ? data.form.responsibleUser?.id ?? null
            : null,
          expirationDate,
          notificationTime,
          categories: data.form.categories,
        },
      },
    })
    if (!result?.data) return

    onShowFiles(result.data.createUserDocument.userDocument)
  }

  async function editDocument(document: UserDocumentRow) {
    const { data } = await addPrompt<EditUserDocumentPromptResolve | null>(
      resolve => (
        <EditUserDocumentPrompt userDocument={document} onSubmit={resolve} />
      )
    )
    if (!data) return

    const { expirationDate, notificationTime } =
      getExpirationAndNotificationTime(data.form, data.hasNotification)

    const result = await mutations.patch({
      variables: {
        id: document.id,
        input: {
          name: data.form.name,
          responsibleUser: data.hasNotification
            ? data.form.responsibleUser?.id ?? null
            : null,
          expirationDate,
          notificationTime,
          categories: data.form.categories,
        },
      },
    })
    if (!result?.data) return

    onShowFiles(result.data.patchUserDocument.userDocument)
  }

  async function removeDocument(document: UserDocumentRow) {
    const { data: answer } = await confirm(
      translations.deleteConfirmation({ name: document.name }),
      translations.delete
    )
    if (!answer) return

    await mutations.delete({ variables: { id: document.id } })
  }

  return { addDocument, editDocument, removeDocument }
}
